/**
 * Relation Tree Builder
 * Transforms flat cluster data into a tree structure for the family tree view.
 * 
 * Input: user anime entries + pending anime + relationsData from a cluster
 * Output: tree structure with nodes and edges for rendering
 */

import { EXCLUDED_DISPLAY_RELATION_TYPES, RELATION_TYPE_INVERSE } from '../config/constants';

/**
 * Build a relation tree for a single group/cluster.
 * 
 * @param {Object} group - A pending group from discoverGroupingService
 *   - group.userGroup.anime[] - user's anime entries in this cluster
 *   - group.pendingAnime[] - pending anime in this cluster
 * @param {Object} relationsData - Full relations data from context (keyed by anime ID)
 * @param {string} titlePreference - User's title preference ('romaji', 'english', 'native')
 * @returns {Object} Tree structure { roots: [], nodes: {}, edges: [] }
 */
export const buildRelationTree = (group, relationsData, titlePreference = 'romaji') => {
    if (!group || !group.userGroup) return { roots: [], nodes: {}, edges: [] };

    const userEntries = group.userGroup.anime || [];
    const pendingAnime = group.pendingAnime || [];

    // Build ID sets
    const userIdSet = new Set(userEntries.map(e => e.media?.id).filter(Boolean));
    const pendingIdSet = new Set(pendingAnime.map(a => a.id));
    const allRelevantIds = new Set([...userIdSet, ...pendingIdSet]);

    // Build node map
    const nodes = {};

    // Add user anime nodes (green)
    userEntries.forEach(entry => {
        const id = entry.media?.id;
        if (!id) return;
        nodes[id] = {
            id,
            title: entry.media?.title, // Store raw object { romaji, english, native } — NOT pre-resolved
            coverImage: entry.media?.coverImage,
            format: entry.media?.format,
            status: entry.media?.status,
            episodes: entry.media?.episodes,
            duration: entry.media?.duration,
            averageScore: entry.media?.averageScore,
            description: entry.media?.description, // Fix #1: include description for overlay
            startDate: entry.media?.startDate,
            isInUserList: true,
            userStatus: entry.status,
            children: [],
            parents: []
        };
    });

    // Add pending anime nodes (dark)
    pendingAnime.forEach(anime => {
        if (nodes[anime.id]) return; // Skip if already added
        nodes[anime.id] = {
            id: anime.id,
            title: anime.title,
            coverImage: anime.coverImage,
            format: anime.format,
            status: anime.status,
            episodes: anime.episodes,
            duration: anime.duration,
            averageScore: anime.averageScore,
            startDate: anime.startDate,
            description: anime.description,
            isInUserList: false,
            relationType: anime.relationType,
            relatedTo: anime.relatedTo,
            children: [],
            parents: []
        };
    });

    // Build adjacency from relationsData (directed edges)
    // An edge from A to B with type X means "B is X of A"
    // e.g., A -> B (SEQUEL) means B is a sequel of A
    const edges = [];
    const edgeSet = new Set(); // Prevent duplicate edges

    Object.entries(relationsData).forEach(([sourceIdStr, relData]) => {
        const sourceId = parseInt(sourceIdStr);
        if (!allRelevantIds.has(sourceId)) return;

        const relEdges = relData?.relations?.edges || [];
        relEdges.forEach(edge => {
            const targetId = edge.node?.id;
            if (!targetId || !allRelevantIds.has(targetId)) return;

            // Skip excluded relation types
            if (EXCLUDED_DISPLAY_RELATION_TYPES.has(edge.relationType)) return;

            // Create a canonical edge key to prevent duplicates
            const edgeKey = `${Math.min(sourceId, targetId)}-${Math.max(sourceId, targetId)}`;
            if (edgeSet.has(edgeKey)) return;
            edgeSet.add(edgeKey);

            // Normalize direction: always parent -> child (earlier -> later in franchise)
            // SEQUEL: source -> target (target is sequel) - natural direction
            // PREQUEL: flip - target -> source (source is the sequel)
            // SIDE_STORY: source -> target (target branches off)
            // PARENT: flip - target -> source (source's parent is target)
            // SOURCE: flip - target -> source
            const FLIP_TYPES = new Set(['PREQUEL', 'PARENT', 'SOURCE']);

            if (FLIP_TYPES.has(edge.relationType)) {
                const childType = RELATION_TYPE_INVERSE[edge.relationType] || edge.relationType;
                edges.push({
                    parentId: targetId,
                    childId: sourceId,
                    relationType: childType
                });
            } else {
                edges.push({
                    parentId: sourceId,
                    childId: targetId,
                    relationType: edge.relationType
                });
            }
        });
    });

    // Build parent-child relationships
    edges.forEach(edge => {
        if (nodes[edge.parentId] && nodes[edge.childId]) {
            nodes[edge.parentId].children.push({
                id: edge.childId,
                relationType: edge.relationType
            });
            nodes[edge.childId].parents.push({
                id: edge.parentId,
                relationType: edge.relationType
            });
        }
    });

    // Find root nodes (nodes with no parents, or the earliest by release date)
    let roots = Object.values(nodes).filter(n => n.parents.length === 0);

    // If no clear root (cycles), pick the earliest user anime by release date
    if (roots.length === 0) {
        const userNodes = Object.values(nodes).filter(n => n.isInUserList);
        if (userNodes.length > 0) {
            userNodes.sort((a, b) => {
                const yearA = a.startDate?.year || 9999;
                const yearB = b.startDate?.year || 9999;
                return yearA - yearB;
            });
            roots = [userNodes[0]];
            roots[0].parents = [];
        }
    }

    // Sort roots by release date
    roots.sort((a, b) => {
        const yearA = a.startDate?.year || 9999;
        const yearB = b.startDate?.year || 9999;
        return yearA - yearB;
    });

    // Prune: only keep nodes reachable from roots that have pending anime in subtree
    // Also keep user anime that are "links in the chain"
    const reachableIds = new Set();
    const hasPendingInSubtree = {};

    const dfs = (nodeId, visited = new Set()) => {
        if (visited.has(nodeId)) return false;
        visited.add(nodeId);
        reachableIds.add(nodeId);

        const node = nodes[nodeId];
        if (!node) return false;

        let subtreeHasPending = !node.isInUserList;

        for (const child of node.children) {
            if (dfs(child.id, visited)) {
                subtreeHasPending = true;
            }
        }

        hasPendingInSubtree[nodeId] = subtreeHasPending;
        return subtreeHasPending;
    };

    roots.forEach(root => dfs(root.id));

    // Keep: pending anime (if reachable) + user anime that lead to pending (chain links)
    const filteredNodeIds = new Set();
    Object.keys(nodes).forEach(idStr => {
        const id = parseInt(idStr);
        if (!reachableIds.has(id)) return;
        if (!nodes[id].isInUserList) {
            filteredNodeIds.add(id); // Pending = always include
        } else if (hasPendingInSubtree[id]) {
            filteredNodeIds.add(id); // User anime = only if chain link
        }
    });

    // Filter children/parents to only include filtered nodes
    Object.values(nodes).forEach(node => {
        node.children = node.children.filter(c => filteredNodeIds.has(c.id));
        node.parents = node.parents.filter(p => filteredNodeIds.has(p.id));
    });

    const filteredRoots = roots
        .filter(r => filteredNodeIds.has(r.id))
        .map(r => r.id);

    return {
        roots: filteredRoots,
        nodes,
        edges,
        stats: {
            totalNodes: filteredNodeIds.size,
            userNodes: [...filteredNodeIds].filter(id => nodes[id]?.isInUserList).length,
            pendingNodes: [...filteredNodeIds].filter(id => !nodes[id]?.isInUserList).length
        }
    };
};

/**
 * Flatten a tree into a depth-first array for rendering.
 * Each item has { node, depth, parentId, relationType, childCount }
 */
export const flattenTree = (tree) => {
    const result = [];
    const visited = new Set();

    const traverse = (nodeId, depth = 0, parentId = null, relationType = null) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);

        const node = tree.nodes[nodeId];
        if (!node) return;

        result.push({
            node,
            depth,
            parentId,
            relationType,
            childCount: node.children.length
        });

        // Sort children: sequels first, then by release date
        const sortedChildren = [...node.children].sort((a, b) => {
            const aIsSequel = a.relationType === 'SEQUEL' ? 0 : 1;
            const bIsSequel = b.relationType === 'SEQUEL' ? 0 : 1;
            if (aIsSequel !== bIsSequel) return aIsSequel - bIsSequel;

            const nodeA = tree.nodes[a.id];
            const nodeB = tree.nodes[b.id];
            const yearA = nodeA?.startDate?.year || 9999;
            const yearB = nodeB?.startDate?.year || 9999;
            return yearA - yearB;
        });

        sortedChildren.forEach(child => {
            traverse(child.id, depth + 1, nodeId, child.relationType);
        });
    };

    tree.roots.forEach(rootId => traverse(rootId));
    return result;
};
